import { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from './client';
import type { SmsCascadePrefetchCache } from './smsCascadePrefetch';
import { getSmsCascadePrefetchCache, prefetchSmsCascadeContext } from './smsCascadePrefetch';

function pickRecord(obj: unknown): Record<string, unknown> | null {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    return obj as Record<string, unknown>;
  }
  return null;
}

/** Строка отправителя из GET …/email/sendersconfirm (`addresses`). */
export type EmailCabinetSenderRow = { id: number; email: string };

/** Список контактов из GET …/email/getlistcontacts (`lists_contacts`). */
export type EmailCabinetListRow = { id: number; name: string; emailsCount?: number };

export type EmailCabinetCatalog = {
  senders: EmailCabinetSenderRow[];
  listsContacts: EmailCabinetListRow[];
  /** GET …/email/getcountemails, поле count */
  totalEmailsCount?: number;
};

export type EmailCabinetFetchState = {
  catalog: EmailCabinetCatalog;
  /** Сообщения при явном fetch (см. fetchEmailCabinetCatalog); при префетче всегда пусто. */
  errors: string[];
};

function numId(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseSendersBody(body: unknown): EmailCabinetSenderRow[] {
  const root = pickRecord(body);
  const raw = root?.addresses;
  if (!Array.isArray(raw)) return [];
  const out: EmailCabinetSenderRow[] = [];
  const seen = new Set<number>();
  for (const it of raw) {
    const o = pickRecord(it);
    if (!o) continue;
    const id = numId(o.id);
    const email = String(o.email ?? '').trim();
    if (id == null || !email || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, email });
  }
  return out.sort((a, b) => a.id - b.id);
}

function parseListsContactsBody(body: unknown): EmailCabinetListRow[] {
  const root = pickRecord(body);
  const raw = root?.lists_contacts ?? root?.listsContacts;
  if (!Array.isArray(raw)) return [];
  const out: EmailCabinetListRow[] = [];
  const seen = new Set<number>();
  for (const it of raw) {
    const o = pickRecord(it);
    if (!o) continue;
    const id = numId(o.id);
    const name = String(o.name ?? '').trim();
    if (id == null || !name || seen.has(id)) continue;
    seen.add(id);
    const ec = o.emails_count ?? o.emailsCount;
    const emailsCount = typeof ec === 'number' && Number.isFinite(ec) ? ec : undefined;
    out.push({ id, name, ...(emailsCount != null ? { emailsCount } : {}) });
  }
  return out.sort((a, b) => a.id - b.id);
}

function parseCountBody(body: unknown): number | undefined {
  const root = pickRecord(body);
  const c = root?.count;
  if (typeof c === 'number' && Number.isFinite(c)) return c;
  return undefined;
}

/** Собрать каталог из кэша префетча (после prefetchSmsCascadeContext). */
export function extractEmailCabinetCatalogFromPrefetchCache(
  cache: SmsCascadePrefetchCache,
): EmailCabinetCatalog {
  return {
    listsContacts: parseListsContactsBody(cache.emailGetlistcontacts),
    senders: parseSendersBody(cache.emailSendersconfirm),
    totalEmailsCount: parseCountBody(cache.emailGetcountemails),
  };
}

/** Строка каталога POST /api/email/templates (samples[]; id — ref для GET /api/email/template/{ref}). */
export type EmailMessageTemplateRow = { ref: string; title: string };

export function parseEmailTemplatesListBody(body: unknown): EmailMessageTemplateRow[] {
  const root = pickRecord(body);
  const raw = root?.samples;
  if (!Array.isArray(raw)) return [];
  const out: EmailMessageTemplateRow[] = [];
  const seen = new Set<string>();
  for (const it of raw) {
    const o = pickRecord(it);
    if (!o) continue;
    const idRaw = o.id ?? o.ref ?? o.sample_id ?? o.sampleId;
    const ref = idRaw != null ? String(idRaw).trim() : '';
    if (!ref || seen.has(ref)) continue;
    seen.add(ref);
    const title = String(o.title ?? o.name ?? o.subject ?? ref).trim() || ref;
    out.push({ ref, title });
  }
  return out;
}

export function extractEmailMessageTemplatesFromPrefetchCache(cache: SmsCascadePrefetchCache): EmailMessageTemplateRow[] {
  return parseEmailTemplatesListBody(cache.emailTemplates);
}

export type EmailTemplateDetail = { title: string; html: string; preheader: string };

export function parseEmailTemplateDetailBody(body: unknown): EmailTemplateDetail {
  const root = pickRecord(body);
  const nested = root?.data;
  const o = pickRecord(nested) ?? root;
  if (!o) return { title: '', html: '', preheader: '' };
  const htmlRaw = o.html ?? o.message ?? o.body ?? '';
  return {
    title: String(o.title ?? o.subject ?? '').trim(),
    html: String(htmlRaw ?? '').trim(),
    preheader: String(o.preheader ?? o.preview_text ?? o.previewText ?? '').trim(),
  };
}

export async function fetchEmailTemplatesCatalogRows(
  opts?: { search?: string; count?: number; offset?: number },
): Promise<EmailMessageTemplateRow[]> {
  const body = await apiPost<unknown>('/api/email/templates', {
    search: opts?.search ?? '',
    count: opts?.count ?? 200,
    offset: opts?.offset ?? 0,
    direction: 'asc',
    order: 'id',
  });
  return parseEmailTemplatesListBody(body);
}

export async function fetchEmailTemplateByRef(ref: string): Promise<EmailTemplateDetail> {
  const r = String(ref || '').trim();
  if (!r) return { title: '', html: '', preheader: '' };
  const body = await apiGet<unknown>(`/api/email/template/${encodeURIComponent(r)}`);
  return parseEmailTemplateDetailBody(body);
}

export function useEmailMessageTemplates(active: boolean): {
  rows: EmailMessageTemplateRow[];
  listError: string | null;
} {
  const [rev, setRev] = useState(0);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void prefetchSmsCascadeContext().then(() => {
      if (!cancelled) setRev((x) => x + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [active]);

  return useMemo(() => {
    if (!active) {
      return { rows: [], listError: null };
    }
    const rows = extractEmailMessageTemplatesFromPrefetchCache(getSmsCascadePrefetchCache());
    return { rows, listError: null };
  }, [active, rev]);
}

/** Параллельная загрузка getlistcontacts + sendersconfirm + getcountemails (без кэша префетча). */
export async function fetchEmailCabinetCatalog(): Promise<EmailCabinetFetchState> {
  const errors: string[] = [];
  let senders: EmailCabinetSenderRow[] = [];
  let listsContacts: EmailCabinetListRow[] = [];
  let totalEmailsCount: number | undefined;

  const results = await Promise.allSettled([
    apiGet<unknown>('/api/email/getlistcontacts'),
    apiGet<unknown>('/api/email/sendersconfirm'),
    apiGet<unknown>('/api/email/getcountemails'),
  ]);

  const labels = ['getlistcontacts', 'sendersconfirm', 'getcountemails'] as const;

  results.forEach((r, i) => {
    const label = labels[i];
    if (r.status === 'fulfilled') {
      const val = r.value;
      if (i === 0) listsContacts = parseListsContactsBody(val);
      else if (i === 1) senders = parseSendersBody(val);
      else totalEmailsCount = parseCountBody(val);
    } else {
      const msg = r.reason instanceof Error ? r.reason.message : String(r.reason);
      errors.push(`${label}: ${msg}`);
    }
  });

  return {
    catalog: { senders, listsContacts, totalEmailsCount },
    errors,
  };
}

const emptyCatalog: EmailCabinetCatalog = { senders: [], listsContacts: [] };

/**
 * Справочники Email из общего префетча (smsCascadePrefetch — те же три GET, что и для узла).
 * При открытии инспектора Email дождёмся завершения prefetch и перечитаем кэш (как SMS/Viber).
 */
export function useEmailCabinetCatalog(active: boolean): EmailCabinetFetchState {
  const [rev, setRev] = useState(0);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void prefetchSmsCascadeContext().then(() => {
      if (!cancelled) setRev((x) => x + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [active]);

  return useMemo(() => {
    if (!active) {
      return { catalog: emptyCatalog, errors: [] };
    }
    const catalog = extractEmailCabinetCatalogFromPrefetchCache(getSmsCascadePrefetchCache());
    return { catalog, errors: [] };
  }, [active, rev]);
}
